import type { StateCreator } from "zustand/vanilla";

/**
 * SearchSliceState is the unified search input: the query text and the
 * viewport-scope toggle.
 */
export interface SearchSliceState {
  /**
   * searchQuery is the text the search tab is searching for, owned here so a
   * human keystroke and an agent-issued query share one source of truth. The
   * field stays an empty string when nothing is being searched; the search
   * effect debounces on this value to drive the unified fan-out.
   */
  searchQuery: string;
  /**
   * searchViewportScope is whether the search restricts to the current map
   * viewport (true) or runs unscoped (false). The flag is owned here so the
   * input toggle and an agent call drive one path.
   */
  searchViewportScope: boolean;
}

/**
 * SearchSliceActions are the whole-slot setters that drive the search input,
 * reached only through the tool bus.
 */
export interface SearchSliceActions {
  setSearchQuery: (query: string) => void;
  setSearchViewportScope: (scoped: boolean) => void;
}

/**
 * searchInitialState seeds an empty query scoped to the viewport.
 */
export const searchInitialState: SearchSliceState = {
  searchQuery: "",
  searchViewportScope: true,
};

/**
 * createSearchSlice builds the search-input whole-slot setters.
 */
export const createSearchSlice: StateCreator<
  SearchSliceState & SearchSliceActions,
  [],
  [],
  SearchSliceActions
> = (set) => ({
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setSearchViewportScope: (searchViewportScope) => set({ searchViewportScope }),
});
